import { Injectable, computed, inject, signal } from '@angular/core';
import { PatrolModQueueItem } from '../models/patrol-discord-cockpit.model';
import {
  PatrolChannelPolicy,
  PatrolDiscordChannelOption,
  PatrolGuildSettings,
  PatrolPolicyPresetId,
} from '../models/patrol-discord-cockpit.model';
import {
  PatrolGuildPoliciesResponse,
  PatrolRobloxApiService,
  PatrolRobloxBridgeStatus,
  PatrolRobloxExperience,
  PatrolRobloxIngestHealth,
  UpdatePatrolGuildPoliciesRequest,
} from './patrol-roblox-api.service';
import { PATROL_KIND_SPEECH_DEFAULTS } from '../constants/patrol-kind-speech-defaults';

export type PatrolRobloxCockpitTab = 'overview' | 'studio' | 'policies' | 'queue';

const DEFAULT_GUILD_SETTINGS: PatrolGuildSettings = {
  defaultPolicyId: 'standard',
  linkRequired: false,
  reputationEnabled: true,
  ageRoleSyncEnabled: false,
  verifiedRoleName: 'Verified',
  ageBrackets: [],
  failClosedOnAiOutage: false,
  notificationsEmail: false,
  ...PATROL_KIND_SPEECH_DEFAULTS,
};

@Injectable({ providedIn: 'root' })
export class PatrolRobloxCockpitService {
  #api = inject(PatrolRobloxApiService);

  readonly #experiences = signal<PatrolRobloxExperience[]>([]);
  readonly #health = signal<PatrolRobloxIngestHealth | null>(null);
  readonly #selectedRegistryId = signal('');
  readonly #activeTab = signal<PatrolRobloxCockpitTab>('overview');
  readonly #isLoadingExperiences = signal(true);
  readonly #isCheckingHealth = signal(true);
  readonly #isAddingExperience = signal(false);
  readonly #experiencesError = signal<string | null>(null);
  readonly #healthError = signal<string | null>(null);
  readonly #modQueueItems = signal<PatrolModQueueItem[]>([]);
  readonly #isLoadingModQueue = signal(false);
  readonly #modQueueError = signal<string | null>(null);
  readonly #modQueueActionId = signal<string | null>(null);
  readonly #guildSettings = signal<Record<string, PatrolGuildSettings>>({});
  readonly #channelPolicies = signal<Record<string, PatrolChannelPolicy[]>>({});
  readonly #discordChannels = signal<Record<string, PatrolDiscordChannelOption[]>>({});
  readonly #isLoadingPolicies = signal(false);
  readonly #isSavingPolicies = signal(false);
  readonly #policiesError = signal<string | null>(null);
  #bridgePollTimer: ReturnType<typeof setInterval> | null = null;

  readonly ingestUrl = this.#api.ingestUrl;
  readonly experiences = this.#experiences.asReadonly();
  readonly health = this.#health.asReadonly();
  readonly selectedRegistryId = this.#selectedRegistryId.asReadonly();
  readonly activeTab = this.#activeTab.asReadonly();
  readonly isLoadingExperiences = this.#isLoadingExperiences.asReadonly();
  readonly isCheckingHealth = this.#isCheckingHealth.asReadonly();
  readonly isAddingExperience = this.#isAddingExperience.asReadonly();
  readonly experiencesError = this.#experiencesError.asReadonly();
  readonly healthError = this.#healthError.asReadonly();
  readonly modQueueItems = this.#modQueueItems.asReadonly();
  readonly isLoadingModQueue = this.#isLoadingModQueue.asReadonly();
  readonly modQueueError = this.#modQueueError.asReadonly();
  readonly modQueueActionId = this.#modQueueActionId.asReadonly();
  readonly isLoadingPolicies = this.#isLoadingPolicies.asReadonly();
  readonly isSavingPolicies = this.#isSavingPolicies.asReadonly();
  readonly policiesError = this.#policiesError.asReadonly();

  readonly selectedContentKey = computed(() => {
    const experience = this.selectedExperience();
    return experience?.communityId?.trim() || '';
  });

  readonly guildSettings = computed(() => {
    const key = this.selectedContentKey();
    return key ? (this.#guildSettings()[key] ?? DEFAULT_GUILD_SETTINGS) : DEFAULT_GUILD_SETTINGS;
  });

  readonly channelPolicies = computed(() => {
    const key = this.selectedContentKey();
    return key ? (this.#channelPolicies()[key] ?? []) : [];
  });

  readonly discordChannels = computed(() => {
    const key = this.selectedContentKey();
    return key ? (this.#discordChannels()[key] ?? []) : [];
  });

  readonly selectedExperience = computed(() =>
    this.#experiences().find((e) => e.registryId === this.#selectedRegistryId()),
  );

  readonly ingestReady = computed(() => Boolean(this.#health()?.ingestEnabled));

  constructor() {
    void this.loadInitialData();
  }

  async loadInitialData(): Promise<void> {
    await Promise.all([this.loadExperiences(), this.checkHealth()]);
  }

  async loadExperiences(options?: { silent?: boolean }): Promise<void> {
    if (!options?.silent) {
      this.#isLoadingExperiences.set(true);
    }
    this.#experiencesError.set(null);
    try {
      const experiences = await this.#api.listExperiences();
      this.#experiences.set(experiences);
      this.#ensureSelection(experiences);
    } catch {
      if (!options?.silent) {
        this.#experiencesError.set('load_failed');
        this.#experiences.set([]);
        this.#selectedRegistryId.set('');
      }
    } finally {
      if (!options?.silent) {
        this.#isLoadingExperiences.set(false);
      }
    }
  }

  startBridgePolling(intervalMs = 20_000): void {
    this.stopBridgePolling();
    this.#bridgePollTimer = setInterval(() => {
      void this.loadExperiences({ silent: true });
    }, intervalMs);
  }

  stopBridgePolling(): void {
    if (this.#bridgePollTimer) {
      clearInterval(this.#bridgePollTimer);
      this.#bridgePollTimer = null;
    }
  }

  async checkHealth(): Promise<void> {
    this.#isCheckingHealth.set(true);
    this.#healthError.set(null);
    try {
      this.#health.set(await this.#api.getIngestHealth());
    } catch {
      this.#health.set(null);
      this.#healthError.set('check_failed');
    } finally {
      this.#isCheckingHealth.set(false);
    }
  }

  async refreshAll(): Promise<void> {
    await Promise.all([this.loadExperiences(), this.checkHealth()]);
  }

  selectExperience(registryId: string): void {
    this.#selectedRegistryId.set(registryId);
    this.#activeTab.set('overview');
    const experience = this.#experiences().find((e) => e.registryId === registryId);
    const communityId = experience?.communityId;
    if (!communityId) {
      return;
    }
    if (this.#activeTab() === 'queue') {
      void this.loadModQueueForExperience(communityId);
    }
    if (this.#activeTab() === 'policies' && experience?.status === 'active') {
      void this.loadPoliciesForExperience(communityId);
    }
  }

  setActiveTab(tab: PatrolRobloxCockpitTab): void {
    this.#activeTab.set(tab);
    const communityId = this.selectedExperience()?.communityId;
    if (!communityId) {
      return;
    }
    if (tab === 'queue') {
      void this.loadModQueueForExperience(communityId);
    }
    if (tab === 'policies' && this.selectedExperience()?.status === 'active') {
      void this.loadPoliciesForExperience(communityId);
    }
  }

  async loadPoliciesForExperience(communityId: string): Promise<void> {
    const key = communityId.trim();
    if (!key) {
      return;
    }

    this.#isLoadingPolicies.set(true);
    this.#policiesError.set(null);
    try {
      const response = await this.#api.getExperiencePolicies(key);
      this.#applyPoliciesResponse(key, response);
    } catch {
      this.#policiesError.set('load_failed');
      this.#guildSettings.update((state) => ({
        ...state,
        [key]: DEFAULT_GUILD_SETTINGS,
      }));
      this.#channelPolicies.update((state) => ({ ...state, [key]: [] }));
      this.#discordChannels.update((state) => ({ ...state, [key]: [] }));
    } finally {
      this.#isLoadingPolicies.set(false);
    }
  }

  channelPolicyForChannel(
    discordChannelId: string,
  ): PatrolPolicyPresetId | 'inherit' {
    const row = this.channelPolicies().find(
      (policy) => policy.discordChannelId === discordChannelId,
    );
    return row?.policyId ?? 'inherit';
  }

  async updateDefaultPolicy(policyId: PatrolPolicyPresetId): Promise<void> {
    const communityId = this.selectedExperience()?.communityId;
    const key = this.selectedContentKey();
    if (!communityId || !key) {
      return;
    }

    this.#guildSettings.update((state) => ({
      ...state,
      [key]: { ...this.guildSettings(), defaultPolicyId: policyId },
    }));
    await this.#savePolicies(communityId, {
      guildSettings: { defaultPolicyId: policyId },
    });
  }

  async updateGuildSetting<K extends keyof PatrolGuildSettings>(
    setting: K,
    value: PatrolGuildSettings[K],
  ): Promise<void> {
    const communityId = this.selectedExperience()?.communityId;
    const key = this.selectedContentKey();
    if (!communityId || !key) {
      return;
    }

    this.#guildSettings.update((state) => ({
      ...state,
      [key]: { ...this.guildSettings(), [setting]: value },
    }));
    await this.#savePolicies(communityId, {
      guildSettings: { [setting]: value },
    });
  }

  async updateChannelPolicy(
    discordChannelId: string,
    policyId: PatrolPolicyPresetId | 'inherit',
  ): Promise<void> {
    const communityId = this.selectedExperience()?.communityId;
    const key = this.selectedContentKey();
    if (!communityId || !key) {
      return;
    }

    const channel = this.discordChannels().find(
      (item) => item.discordChannelId === discordChannelId,
    );
    if (!channel) {
      return;
    }

    const existing = this.channelPolicies().filter(
      (row) => row.discordChannelId !== discordChannelId,
    );
    const nextRows =
      policyId === 'inherit'
        ? existing
        : [
            ...existing,
            {
              id: discordChannelId,
              discordChannelId,
              channelName: channel.channelName,
              channelType: channel.channelType,
              policyId,
            },
          ];

    this.#channelPolicies.update((state) => ({
      ...state,
      [key]: nextRows,
    }));

    await this.#savePolicies(communityId, {
      channelPolicies: nextRows.map((row) => ({
        discordChannelId: row.discordChannelId,
        policyId: row.policyId,
      })),
    });
  }

  async #savePolicies(
    communityId: string,
    body: UpdatePatrolGuildPoliciesRequest,
  ): Promise<void> {
    const key = this.selectedContentKey();
    if (!key) {
      return;
    }

    this.#isSavingPolicies.set(true);
    this.#policiesError.set(null);
    try {
      const response = await this.#api.updateExperiencePolicies(communityId, body);
      this.#applyPoliciesResponse(key, response);
    } catch {
      this.#policiesError.set('save_failed');
    } finally {
      this.#isSavingPolicies.set(false);
    }
  }

  #applyPoliciesResponse(key: string, response: PatrolGuildPoliciesResponse): void {
    this.#guildSettings.update((state) => ({
      ...state,
      [key]: response.guildSettings,
    }));
    this.#channelPolicies.update((state) => ({
      ...state,
      [key]: response.channelPolicies,
    }));
    this.#discordChannels.update((state) => ({
      ...state,
      [key]: response.discordChannels,
    }));
  }

  async loadModQueueForExperience(communityId: string): Promise<void> {
    this.#isLoadingModQueue.set(true);
    this.#modQueueError.set(null);
    try {
      const items = await this.#api.listModQueue(communityId);
      this.#modQueueItems.set(items);
    } catch {
      this.#modQueueError.set('load_failed');
      this.#modQueueItems.set([]);
    } finally {
      this.#isLoadingModQueue.set(false);
    }
  }

  async approveModQueueItem(id: string): Promise<boolean> {
    const communityId = this.selectedExperience()?.communityId;
    if (!communityId) {
      return false;
    }

    this.#modQueueError.set(null);
    this.#modQueueActionId.set(id);
    try {
      await this.#api.approveModQueueItem(id);
      await this.loadModQueueForExperience(communityId);
      return true;
    } catch {
      this.#modQueueError.set('action_failed');
      return false;
    } finally {
      this.#modQueueActionId.set(null);
    }
  }

  async rejectModQueueItem(id: string): Promise<boolean> {
    const communityId = this.selectedExperience()?.communityId;
    if (!communityId) {
      return false;
    }

    this.#modQueueError.set(null);
    this.#modQueueActionId.set(id);
    try {
      await this.#api.rejectModQueueItem(id);
      await this.loadModQueueForExperience(communityId);
      return true;
    } catch {
      this.#modQueueError.set('action_failed');
      return false;
    } finally {
      this.#modQueueActionId.set(null);
    }
  }

  async addExperience(
    displayName: string,
    universeId: string,
  ): Promise<PatrolRobloxExperience | null> {
    const name = displayName.trim();
    const id = universeId.trim();
    if (!name || !id || !/^\d+$/.test(id)) {
      return null;
    }

    this.#isAddingExperience.set(true);
    try {
      const created = await this.#api.createExperience(name, id);
      this.#experiences.set([...this.#experiences(), created]);
      this.#selectedRegistryId.set(created.registryId);
      this.#activeTab.set('overview');
      return created;
    } catch {
      return null;
    } finally {
      this.#isAddingExperience.set(false);
    }
  }

  formatDate(iso: string | undefined | null): string {
    if (!iso) {
      return '—';
    }
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));
  }

  bridgeStatusLabelKey(
    status: PatrolRobloxBridgeStatus | null | undefined,
  ): string {
    return `app.patrol.roblox.cockpit.bridgeStatus.${status ?? 'never_seen'}`;
  }

  #ensureSelection(experiences: PatrolRobloxExperience[]): void {
    if (experiences.length === 0) {
      this.#selectedRegistryId.set('');
      return;
    }
    const current = this.#selectedRegistryId();
    if (current && experiences.some((e) => e.registryId === current)) {
      return;
    }
    const preferred =
      experiences.find((e) => e.status === 'active') ?? experiences[0];
    this.#selectedRegistryId.set(preferred.registryId);
  }
}
